// src/components/UpgradePage.jsx
// Pricing overlay: monthly/yearly toggle, 3 plans, Stripe Checkout handoff.
import React, { useState } from 'react'
import { useAuth } from '../lib/AuthContext'
import { PLANS, createCheckoutSession } from '../lib/stripe'

export default function UpgradePage({ onClose }) {
  const { user, tier } = useAuth()
  const [billing, setBilling] = useState('yearly')
  const [loadingId, setLoadingId] = useState(null)
  const [error, setError] = useState('')

  async function handleSelect(plan) {
    if (!plan.priceIds) {
      onClose?.()
      return
    }
    if (!user) {
      setError('Sign in first to subscribe.')
      return
    }
    const priceId = plan.priceIds[billing]
    setLoadingId(plan.id)
    setError('')
    try {
      const url = await createCheckoutSession(priceId, user.id, user.email)
      window.location.href = url
    } catch (err) {
      setError('Could not start checkout. Try again.')
      setLoadingId(null)
    }
  }

  function priceLabel(plan) {
    if (plan.id === 'free') return 'Free'
    const v = billing === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice
    return `$${v.toFixed(2)}`
  }

  function priceUnit(plan) {
    if (plan.id === 'free') return 'forever'
    return billing === 'monthly' ? '/mo' : '/yr'
  }

  return (
    <div style={s.overlay} role="dialog" aria-modal="true">
      <div style={s.scroll}>
        <div style={s.header}>
          <button style={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
          <div style={s.title}>Choose your plan</div>
          <div style={s.subtitle}>Cancel anytime. Pricing in USD.</div>
        </div>

        <div style={s.toggleWrap}>
          <button
            style={{ ...s.toggleBtn, ...(billing === 'monthly' ? s.toggleBtnActive : {}) }}
            onClick={() => setBilling('monthly')}
          >
            Monthly
          </button>
          <button
            style={{ ...s.toggleBtn, ...(billing === 'yearly' ? s.toggleBtnActive : {}) }}
            onClick={() => setBilling('yearly')}
          >
            Yearly
            <span style={s.savePill}>Save 42%</span>
          </button>
        </div>

        {error && <div style={s.errorMsg}>{error}</div>}

        <div style={s.plans}>
          {PLANS.map(plan => {
            const isCurrent = plan.id === tier
            const isLoading = loadingId === plan.id
            return (
              <div
                key={plan.id}
                style={{
                  ...s.planCard,
                  ...(plan.featured ? s.planCardFeatured : {}),
                }}
              >
                {plan.featured && <div style={s.popularBadge}>Most Popular</div>}

                <div style={s.planHead}>
                  <div style={s.planEmoji}>{plan.emoji}</div>
                  <div style={s.planName}>{plan.name}</div>
                  <div style={s.planDesc}>{plan.description}</div>
                </div>

                <div style={s.priceRow}>
                  <span style={s.price}>{priceLabel(plan)}</span>
                  <span style={s.priceUnit}>{priceUnit(plan)}</span>
                </div>

                <div style={s.features}>
                  {plan.features.map((f, i) => (
                    <div
                      key={i}
                      style={{ ...s.featureRow, color: f.included ? '#d4d1cb' : '#444' }}
                    >
                      <span style={{ color: f.included ? '#4caf7d' : '#444', fontSize: 12 }}>
                        {f.included ? '✓' : '✕'}
                      </span>
                      {f.text}
                    </div>
                  ))}
                </div>

                <button
                  style={{
                    ...s.cta,
                    ...(plan.featured ? s.ctaPrimary : s.ctaSecondary),
                    opacity: isLoading || isCurrent ? 0.6 : 1,
                    cursor: isCurrent ? 'default' : 'pointer',
                  }}
                  disabled={isLoading || isCurrent}
                  onClick={() => handleSelect(plan)}
                >
                  {isCurrent
                    ? 'Current plan'
                    : isLoading
                      ? 'Opening checkout...'
                      : plan.id === 'free'
                        ? 'Continue with Free'
                        : `Choose ${plan.name} →`}
                </button>
              </div>
            )
          })}
        </div>

        <div style={s.footnote}>
          Secure checkout by Stripe. We never see your card details.
        </div>
      </div>
    </div>
  )
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'rgba(10,10,10,0.96)',
    backdropFilter: 'blur(6px)',
    overflowY: 'auto',
  },
  scroll: {
    maxWidth: 'var(--max-width, 720px)', margin: '0 auto',
    padding: '24px 20px 60px',
  },
  header: { position: 'relative', textAlign: 'center', padding: '8px 0 18px' },
  closeBtn: {
    position: 'absolute', top: 0, right: 0,
    background: 'transparent', border: '1px solid rgba(255,255,255,0.12)',
    color: '#aaa', borderRadius: 8, width: 34, height: 34,
    fontSize: 14, cursor: 'pointer', fontFamily: 'Courier New, monospace',
  },
  title: {
    fontSize: 22, fontWeight: 700, color: '#f0ede8',
    fontFamily: 'Georgia, serif', letterSpacing: '0.01em',
  },
  subtitle: {
    fontSize: 11, color: '#666', fontFamily: 'Courier New, monospace',
    letterSpacing: '0.06em', marginTop: 6, textTransform: 'uppercase',
  },

  toggleWrap: {
    display: 'flex', gap: 6, padding: 5,
    background: '#141414', border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 999, margin: '8px auto 18px', width: 'fit-content',
  },
  toggleBtn: {
    background: 'transparent', border: 'none', color: '#888',
    padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
    fontFamily: 'Courier New, monospace', fontSize: 12,
    letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 8,
  },
  toggleBtnActive: {
    background: '#c8a96e', color: '#0a0a0a', fontWeight: 700,
  },
  savePill: {
    fontSize: 9, padding: '2px 6px', borderRadius: 999,
    background: 'rgba(76,175,125,0.18)', color: '#4caf7d',
    border: '1px solid rgba(76,175,125,0.3)', letterSpacing: '0.04em',
  },

  errorMsg: {
    fontSize: 11, color: '#e05c5c', fontFamily: 'Courier New, monospace',
    padding: '8px 12px', background: 'rgba(224,92,92,0.08)',
    borderRadius: 6, margin: '0 4px 12px',
  },

  plans: { display: 'flex', flexDirection: 'column', gap: 14 },
  planCard: {
    position: 'relative',
    background: '#111', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14, padding: '20px 18px',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  planCardFeatured: {
    border: '1px solid rgba(200,169,110,0.5)',
    boxShadow: '0 0 0 1px rgba(200,169,110,0.25), 0 8px 30px rgba(200,169,110,0.06)',
    background: 'linear-gradient(180deg, rgba(200,169,110,0.05) 0%, #111 60%)',
  },
  popularBadge: {
    position: 'absolute', top: -10, left: 16,
    background: '#c8a96e', color: '#0a0a0a',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
    fontFamily: 'Courier New, monospace', textTransform: 'uppercase',
    padding: '4px 10px', borderRadius: 999,
  },

  planHead: { display: 'flex', flexDirection: 'column', gap: 4 },
  planEmoji: { fontSize: 22 },
  planName: { fontSize: 18, fontWeight: 700, color: '#f0ede8', fontFamily: 'Georgia, serif' },
  planDesc: { fontSize: 11, color: '#888', fontFamily: 'Courier New, monospace', lineHeight: 1.5 },

  priceRow: { display: 'flex', alignItems: 'baseline', gap: 6 },
  price: { fontSize: 28, fontWeight: 700, color: '#c8a96e', fontFamily: 'Georgia, serif' },
  priceUnit: { fontSize: 11, color: '#666', fontFamily: 'Courier New, monospace', letterSpacing: '0.04em' },

  features: {
    display: 'flex', flexDirection: 'column', gap: 7,
    borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12,
  },
  featureRow: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, fontFamily: 'Courier New, monospace' },

  cta: {
    border: 'none', borderRadius: 9, padding: '13px 16px',
    fontSize: 13, fontWeight: 700, fontFamily: 'Georgia, serif',
    letterSpacing: '0.02em', textAlign: 'center', marginTop: 4,
  },
  ctaPrimary:   { background: '#c8a96e', color: '#0a0a0a' },
  ctaSecondary: { background: 'transparent', color: '#c8a96e', border: '1px solid rgba(200,169,110,0.3)' },

  footnote: {
    marginTop: 20, textAlign: 'center', fontSize: 10, color: '#444',
    fontFamily: 'Courier New, monospace', letterSpacing: '0.04em',
  },
}
